import downloadURL from 'libs/downloadURL';
import ModalComponents from 'components/ModalComponents';
import { RawHTML } from '@xbcb/static-text-components';
import safeStringify from 'libs/safeStringify';
import { notification, message, Modal } from 'antd';
import { UiStage } from '@xbcb/ui-types';
import { store } from 'store';
import { getEnv, reportError, openFetchDocTab } from '@xbcb/ui-utils';
import { timeout } from '@xbcb/js-utils';
import { mqtt5, iot } from 'aws-iot-device-sdk-v2';
import { createAction } from 'redux-actions';
import {
  fromCognitoIdentityPool,
  CognitoIdentityCredentialProvider,
} from '@aws-sdk/credential-provider-cognito-identity';
import { CognitoIdentityClient } from '@aws-sdk/client-cognito-identity';

class CognitoCRTProvider {
  private creds?: any;
  private expires?: Date;
  private readonly cognitoProvider: CognitoIdentityCredentialProvider;

  constructor() {
    const { GUEST_COGNITO_IDENTITY_POOL_ID: cognitoIdentityPoolId } = getEnv();
    this.cognitoProvider = fromCognitoIdentityPool({
      client: new CognitoIdentityClient({ region: 'us-west-2' }),
      identityPoolId: cognitoIdentityPoolId,
    });
  }
  async refreshCredentials() {
    const { accessKeyId, secretAccessKey, sessionToken, expiration } =
      await this.cognitoProvider();
    this.creds = {
      aws_access_id: accessKeyId,
      aws_secret_key: secretAccessKey,
      aws_sts_token: sessionToken,
      aws_region: 'us-west-2',
    };
    this.expires = expiration ?? new Date(Date.now() + 10 * 60 * 1000);
  }

  getCredentials() {
    return this.creds;
  }
}

let client: any;

export const MQTT_LOADED = createAction('MQTT_LOADED');

export const MQTT_DISCONNECT = () => async (dispatch: any) => {
  if (client) {
    await client.disconnect();
    client = undefined;
    dispatch(MQTT_LOADED(false));
  }
};

const SHOW_MESSAGE =
  ({
    level = 'info',
    message: messageText, // don't want to conflict with antd message
  }: any) =>
  () => {
    try {
      (message as any)[level](messageText);
    } catch (e) {
      reportError(e);
    }
  };

const SHOW_NOTIFICATION =
  ({ level = 'info', message: messageText, description, duration = 5 }: any) =>
  () => {
    try {
      (notification as any)[level]({
        message: messageText,
        description: <RawHTML html={description} />,
        duration,
      });
    } catch (e) {
      reportError(e);
    }
  };

const DOWNLOAD_URL =
  ({ url, fileName, newTab }: any) =>
  () => {
    if (newTab) {
      const w = openFetchDocTab();
      if (w) w.location.href = url;
    } else {
      try {
        downloadURL(url, fileName);
      } catch (e) {
        reportError(e);
      }
    }
  };

const SHOW_MODAL =
  ({
    level = 'success',
    title,
    component,
    props = {},
    content,
    shouldRefresh,
    ...otherModalProps
  }: any) =>
  async () => {
    const C = ModalComponents(component);
    try {
      const modal = (Modal as any)[level]();
      const modalUpdate = {
        title,
        content: C ? (
          <C {...props} modal={modal} store={store} />
        ) : (
          <RawHTML html={content} />
        ),
        okText: shouldRefresh ? 'Refresh' : 'Close',
        onOk: shouldRefresh ? () => window.location.reload() : undefined,
        width: 550,
        ...otherModalProps,
      };
      modal.update(modalUpdate);
      await timeout(0); // the modal is referenced asynchronously so we need to wait a render cycle for it to appear.
      const modalElement = document.querySelector('.ant-modal-wrap'); // this isn't great to do w/ React but I don't see an easy way to get a ref to the DOM element.
      if (modalElement) {
        modalElement.scrollTop = 0; // really big modals overflow the screen and by default the middle is centered on the screen. We always want the top of the modal to be visible.
      }
    } catch (e) {
      reportError(e);
    }
  };

const stageMap = {
  [UiStage.LOCAL]: UiStage.GAMMA.toLowerCase(),
  [UiStage.ALPHA]: UiStage.BETA.toLowerCase(),
  [UiStage.BETA]: UiStage.BETA.toLowerCase(),
  [UiStage.GAMMA]: UiStage.GAMMA.toLowerCase(),
  [UiStage.PROD]: UiStage.PROD.toLowerCase(),
};

export const MQTT_CONNECT = (cbmsUserId?: string) => async (dispatch: any) => {
  try {
    const { stage, AWS_IOT_ENDPOINT: host } = getEnv();

    if (!cbmsUserId) {
      throw new Error('cbmsUserId is required for MQTT connection');
    }

    const crtProvider = new CognitoCRTProvider();
    await crtProvider.refreshCredentials();

    const clientId = `cbms-${stage}-${Date.now()}`;

    const builder =
      iot.AwsIotMqtt5ClientConfigBuilder.newWebsocketMqttBuilderWithSigv4Auth(
        host,
        {
          credentialsProvider: crtProvider,
          region: 'us-west-2',
        },
      );

    builder.withConnectProperties({
      clientId,
      keepAliveIntervalSeconds: 1200,
    });

    client = new mqtt5.Mqtt5Client(builder.build());

    const dispatchAction = (messageObject: any) => {
      if (messageObject.type) {
        try {
          switch (messageObject.type) {
            case 'SHOW_MESSAGE':
              dispatch(SHOW_MESSAGE(messageObject));
              break;
            case 'SHOW_NOTIFICATION':
              dispatch(SHOW_NOTIFICATION(messageObject));
              break;
            case 'SHOW_MODAL':
              dispatch(SHOW_MODAL(messageObject));
              break;
            case 'DOWNLOAD_URL':
              dispatch(DOWNLOAD_URL(messageObject));
              break;
            default:
              dispatch(messageObject);
          }
        } catch (e) {
          reportError(e);
        }
      }
    };

    client.on('error', (error: any) => {
      reportError(new Error(safeStringify(error)));
    });

    client.on('connectionSuccess', async () => {
      await client.subscribe({
        subscriptions: [
          {
            qos: mqtt5.QoS.AtLeastOnce,
            topicFilter: `cbms-app/${stageMap[stage]}/${cbmsUserId}`,
          },
        ],
      });
      dispatch(MQTT_LOADED(true));
    });

    client.on('messageReceived', (event: any) => {
      if (!event?.message?.payload) return;
      const messageObject = JSON.parse(event.message.payload.toString());
      dispatchAction(messageObject);
    });

    client.on('disconnect', async () => {
      await dispatch(MQTT_DISCONNECT());
      await dispatch(MQTT_CONNECT(cbmsUserId));
    });

    client.on(
      'connectionFailure',
      (eventData: mqtt5.ConnectionFailureEvent) => {
        reportError('Connection failure event: ' + eventData.error.toString());
      },
    );

    client.start();
  } catch (e) {
    reportError(e);
    throw new Error('Unable to authenticate and connect to MQTT');
  }
};
